import { useParams } from "react-router-dom";
import { useProgramMembership } from "../ProgramMemberships/programMembershipUtils";
import { Alert, Box, Chip, CircularProgress, Typography } from "@mui/material";
import { IdleWarning } from "../../common-ui/IdleWarning";
import { useTranslation } from "react-i18next";
import { APIError } from "../../common-ui/APIError";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CheckIcon from "@mui/icons-material/Check";
import { ProgramMembership, ProgramRole } from "../../API";
import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { GroupingMode, GroupingOption, QueryStatus } from "../../app/types";
import { useState } from "react";
import { CCCAssessmentEventTable } from "./CCCAssessmentEventTable";
import { ReportsDeprecationBanner } from "./ReportsDeprecationBanner";
import { UserStats } from "../Users/UserStats";
import { useAppSelector } from "../../app/hooks";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsStatus
} from "../Firebase/firebaseSlice";
import { ProgramRoleSelectFieldsNew } from "../../app/constants";

const ProgramMembershipInfo = ({
  programMembership,
  status,
  error
}: {
  programMembership?: ProgramMembership | null;
  status: QueryStatus;
  error?: string | null;
}) => {
  const user = programMembership?.programMember as unknown as
    | { fullName: string; pgy?: number }
    | undefined;
  const userName = user?.fullName;
  const institutionName = programMembership?.program?.institution?.name;
  let programName = programMembership?.program?.name;
  const usersPGY = (user as any)?.pgy as number | undefined;
  if (usersPGY) {
    programName += ` | PGY${usersPGY}`;
  }
  const programRole = programMembership?.programRole;
  const roleLabel =
    ProgramRoleSelectFieldsNew.find((r) => r.value === programRole)?.label ??
    programRole;

  if (status === "idle") return <IdleWarning />;
  if (status === "loading") return <CircularProgress />;
  if (status === "failed") return <APIError error={JSON.stringify(error)} />;

  return (
    <Box sx={{ py: 0.5 }}>
      {programMembership && (
        <>
          <div>
            <strong>{userName}</strong>
          </div>
          <div>{institutionName}</div>
          <div>{programName}</div>
          {programRole != null && <div>{roleLabel}</div>}
        </>
      )}
    </Box>
  );
};

// Should probably create a DataGrid wrapper that does this declaration so that we don't have to decalre this each time it's used
declare module "@mui/x-data-grid-premium" {
  interface LoadingOverlayPropsOverrides {
    message: string;
  }
}

const pageDetails: PageWrapperProps = {
  className: "UserReport",
  headerText: "CCC Report",
  headerVariant: "h2"
};

const GroupingOptions: GroupingOption[] = [
  {
    label: "No Grouping",
    value: "none"
  },
  {
    label: "Activity",
    value: "activity"
  },
  {
    label: "Milestone",
    value: "milestone"
  }
];

export const UserReport = () => {
  const { t } = useTranslation();
  const { programMembershipId, userId, programId } = useParams();
  const flags = useAppSelector(selectFeatureFlags);
  const flagsStatus = useAppSelector(selectFeatureFlagsStatus);
  const flagsError = useAppSelector(selectFeatureFlagsError);
  const {
    programMembership,
    status: programMembershipStatus,
    error: programMembershipError
  } = useProgramMembership({ programMembershipId, userId, programId });

  const role = programMembership?.programRole;

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [groupingMode, setGroupingMode] = useState<GroupingMode>("none");

  const fileName = `${
    (
      programMembership?.programMember as unknown as
        | { fullName: string }
        | undefined
    )?.fullName
  }_${programMembership?.program?.institution?.name}_${
    programMembership?.program?.name
  }`;

  const { user_ccc_reports_enabled, enable_milestone_ccc } = flags;
  const necessaryDataFetchStatuses = [flagsStatus, programMembershipStatus];
  const necessaryDataFetchErrors = [flagsError, programMembershipError];
  const allNecessaryDataFetched = necessaryDataFetchStatuses.every(
    (status) => status === "succeeded"
  );

  if (necessaryDataFetchStatuses.every((status) => status === "idle"))
    return <IdleWarning />;
  if (necessaryDataFetchStatuses.includes("loading"))
    return <CircularProgress />;
  if (necessaryDataFetchStatuses.includes("failed"))
    return (
      <APIError
        error={JSON.stringify(
          necessaryDataFetchErrors.find((error) => error) || "API Error"
        )}
      />
    );

  if (allNecessaryDataFetched && !user_ccc_reports_enabled) {
    return (
      <div>
        <Alert severity="warning">{t("User CCC Reports Disabled")}</Alert>
      </div>
    );
  }

  if (allNecessaryDataFetched && role !== ProgramRole.Trainee) {
    return (
      <div>
        <Alert severity="warning">
          {t("User CCC Reports Are Only Availabe for Trainees.")}
        </Alert>
      </div>
    );
  }

  if (allNecessaryDataFetched && user_ccc_reports_enabled) {
    return (
      <PageWrapper {...pageDetails}>
        <ReportsDeprecationBanner />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 3,
            columnGap: { xs: 2, md: 20 },
            alignItems: "flex-start",
            py: 0.5
          }}
        >
          <Box sx={{ flex: "0 0 auto", minWidth: 0 }}>
            <ProgramMembershipInfo
              programMembership={programMembership}
              status={programMembershipStatus}
              error={programMembershipError}
            />
          </Box>
          <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
            <UserStats userId={programMembership?.programMemberId} />
          </Box>
        </Box>

        {/* Date Selection */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 1,
            py: 0.5
          }}
        >
          <div>
            <DatePicker
              label={t("From Date")}
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
            />
          </div>
          <div>
            <DatePicker
              label={t("To Date")}
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
            />
          </div>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            py: 0.5,
            gap: 1,
            alignItems: "center"
          }}
        >
          <Typography>{t("Group by:")}</Typography>
          {GroupingOptions.map((groupingOption) => {
            if (groupingOption.value === "milestone" && !enable_milestone_ccc)
              return;
            const isSelected = groupingOption.value === groupingMode;
            return (
              <Chip
                onClick={() => setGroupingMode(groupingOption.value)}
                label={t(groupingOption.label)}
                icon={isSelected ? <CheckIcon /> : undefined}
                color="primary"
              />
            );
          })}
        </Box>
        <CCCAssessmentEventTable
          programMembership={programMembership}
          fileName={fileName}
          fromDate={fromDate}
          toDate={toDate}
          groupingMode={groupingMode}
        />
      </PageWrapper>
    );
  }

  return null;
};
